import { useCallback } from 'react';
import { useNotification } from '../contexts/NotificationContext';

interface ErrorResponse {
  response?: {
    data?: {
      detail?: string;
      error?: string;
      message?: string;
      non_field_errors?: string[];
    };
    status?: number;
  };
  message?: string;
  name?: string;
}

interface ErrorHandlerOptions {
  showNotification?: boolean;
  logError?: boolean;
  title?: string;
  fallbackMessage?: string;
}

export const useErrorHandler = () => {
  const { showNotification } = useNotification();

  const extractErrorMessage = useCallback((error: ErrorResponse): string => {
    if (error.response?.data) {
      const data = error.response.data;
      
      if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
        return data.non_field_errors[0];
      }
      
      return data.detail || data.error || data.message || 'Request failed';
    }
    
    if (error.name === 'NetworkError' || error.message?.includes('Network')) {
      return 'Network connection failed. Please check your internet connection.';
    }
    
    if (error.message?.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    
    return error.message || 'An unexpected error occurred';
  }, []);

  const getErrorSeverity = useCallback((error: ErrorResponse): 'error' | 'warning' => {
    if (error.response?.status) {
      const status = error.response.status;
      if (status >= 500) return 'error';
      if (status >= 400) return 'warning';
    }
    return 'error';
  }, []);

  const handleError = useCallback((
    error: ErrorResponse, 
    options: ErrorHandlerOptions = {}
  ) => {
    const {
      showNotification: shouldShowNotification = true,
      logError = true,
      title = 'Error',
      fallbackMessage,
    } = options;

    if (logError) {
      console.error('Error handled:', error);
    }

    const errorMessage = fallbackMessage || extractErrorMessage(error);
    const severity = getErrorSeverity(error);

    if (shouldShowNotification) {
      showNotification({
        type: severity,
        title,
        message: errorMessage,
        duration: severity === 'error' ? 7000 : 5000,
      });
    }

    return errorMessage;
  }, [showNotification, extractErrorMessage, getErrorSeverity]);

  const handleApiError = useCallback((
    error: ErrorResponse, 
    operation: string,
    options: Omit<ErrorHandlerOptions, 'fallbackMessage'> = {}
  ) => {
    const fallbackMessage = `Failed to ${operation}. Please try again.`;
    return handleError(error, { ...options, fallbackMessage });
  }, [handleError]);

  const handleValidationError = useCallback((
    error: ErrorResponse,
    options: ErrorHandlerOptions = {}
  ) => {
    return handleError(error, {
      ...options,
      title: 'Validation Error',
      fallbackMessage: 'Please check your input and try again.',
    });
  }, [handleError]);

  const handleNetworkError = useCallback((
    error: ErrorResponse,
    options: ErrorHandlerOptions = {}
  ) => {
    return handleError(error, {
      ...options,
      title: 'Connection Error',
      fallbackMessage: 'Unable to connect to the server. Please check your internet connection.',
    });
  }, [handleError]);

  return {
    handleError,
    handleApiError,
    handleValidationError,
    handleNetworkError,
    extractErrorMessage,
  };
};